import { useEffect, useRef, useState } from "react";

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Rajdhani:wght@400;500;600;700&family=Orbitron:wght@400;600;700;900&display=swap');

  .about-section {
    position: relative;
    z-index: 1;
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 100px 4rem 80px;
    font-family: 'Rajdhani', sans-serif;
  }

  .about-container {
    width: 100%;
    max-width: 1100px;
    margin: 0 auto;
  }

  /* SECTION HEADER */
  .about-header {
    text-align: center;
    margin-bottom: 4rem;
  }

  .about-label {
    font-family: 'Orbitron', monospace;
    font-size: 0.65rem;
    font-weight: 600;
    color: #ff8c00;
    letter-spacing: 5px;
    text-transform: uppercase;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    margin-bottom: 1rem;
    opacity: 0;
    animation: aboutFadeUp 0.7s ease 0.1s both;
  }

  .about-label::before, .about-label::after {
    content: '';
    width: 50px;
    height: 1px;
  }
  .about-label::before { background: linear-gradient(to right, transparent, #ff8c00); }
  .about-label::after { background: linear-gradient(to left, transparent, #ff8c00); }

  .about-title {
    font-family: 'Orbitron', monospace;
    font-size: clamp(1.8rem, 4vw, 2.8rem);
    font-weight: 900;
    background: linear-gradient(135deg, #ffffff 0%, #38c8ff 60%, #00aaff 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    filter: drop-shadow(0 0 20px rgba(0,170,255,0.3));
    opacity: 0;
    animation: aboutFadeUp 0.7s ease 0.2s both;
  }

  /* MAIN GRID */
  .about-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 2rem;
    margin-bottom: 2.5rem;
  }

  /* LEFT — INTRO CARD */
  .about-intro-card {
    background: rgba(0, 20, 50, 0.5);
    border: 1px solid rgba(0,170,255,0.15);
    border-top: 1px solid rgba(0,170,255,0.4);
    padding: 2rem;
    position: relative;
    opacity: 0;
    animation: aboutFadeUp 0.7s ease 0.3s both;
  }

  .about-intro-card::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 1px;
    background: linear-gradient(to right, transparent, #00aaff, transparent);
  }

  .card-eyebrow {
    font-family: 'Orbitron', monospace;
    font-size: 0.6rem;
    color: #00aaff;
    letter-spacing: 3px;
    text-transform: uppercase;
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
    gap: 0.6rem;
  }

  .card-eyebrow::before {
    content: '';
    display: inline-block;
    width: 20px;
    height: 1px;
    background: #00aaff;
  }

  .about-intro-card h3 {
    font-family: 'Orbitron', monospace;
    font-size: 1.1rem;
    font-weight: 700;
    color: #e8f4ff;
    margin-bottom: 1.2rem;
  }

  .about-intro-card p {
    font-size: 1rem;
    line-height: 1.85;
    color: rgba(232,244,255,0.6);
    margin-bottom: 1rem;
  }

  .about-intro-card p:last-child {
    margin-bottom: 0;
  }

  .highlight-text {
    color: #00aaff;
    font-weight: 600;
  }

  .highlight-orange {
    color: #ffaa33;
    font-weight: 600;
  }

  /* RIGHT COLUMN */
  .about-right {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }

  /* INTEREST CARD */
  .about-interest-card {
    background: rgba(0, 20, 50, 0.5);
    border: 1px solid rgba(0,170,255,0.15);
    border-top: 1px solid rgba(255,140,0,0.4);
    padding: 1.5rem 2rem;
    position: relative;
    opacity: 0;
    animation: aboutFadeUp 0.7s ease 0.4s both;
  }

  .about-interest-card::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 1px;
    background: linear-gradient(to right, transparent, #ff8c00, transparent);
  }

  .interest-items {
    display: flex;
    flex-direction: column;
    gap: 0.8rem;
    margin-top: 1rem;
  }

  .interest-item {
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  .interest-icon {
    width: 38px;
    height: 38px;
    background: rgba(0,170,255,0.08);
    border: 1px solid rgba(0,170,255,0.25);
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1rem;
    flex-shrink: 0;
  }

  .interest-info h4 {
    font-family: 'Orbitron', monospace;
    font-size: 0.75rem;
    font-weight: 600;
    color: #00aaff;
    letter-spacing: 1px;
  }

  .interest-info p {
    font-size: 0.85rem;
    color: rgba(232,244,255,0.5);
    margin-top: 0.15rem;
  }

  /* GOAL CARD */
  .about-goal-card {
    background: rgba(255,140,0,0.04);
    border: 1px solid rgba(255,140,0,0.15);
    border-left: 2px solid #ff8c00;
    padding: 1.2rem 1.5rem;
    opacity: 0;
    animation: aboutFadeUp 0.7s ease 0.5s both;
  }

  .goal-label {
    font-family: 'Orbitron', monospace;
    font-size: 0.6rem;
    color: #ffaa33;
    letter-spacing: 3px;
    text-transform: uppercase;
    margin-bottom: 0.6rem;
  }

  .goal-text {
    font-size: 1rem;
    line-height: 1.7;
    color: rgba(232,244,255,0.65);
  }

  /* STRENGTHS ROW */
  .about-strengths {
    opacity: 0;
    animation: aboutFadeUp 0.7s ease 0.6s both;
  }

  .strengths-title {
    font-family: 'Orbitron', monospace;
    font-size: 0.65rem;
    color: #00aaff;
    letter-spacing: 3px;
    text-transform: uppercase;
    text-align: center;
    margin-bottom: 1.2rem;
  }

  .strengths-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 1rem;
  }

  .strength-card {
    background: rgba(0, 20, 50, 0.5);
    border: 1px solid rgba(0,170,255,0.12);
    padding: 1.2rem 1rem;
    text-align: center;
    position: relative;
    transition: border-color 0.3s, box-shadow 0.3s, transform 0.3s;
    cursor: default;
  }

  .strength-card::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 1px;
    background: linear-gradient(to right, transparent, rgba(0,170,255,0.5), transparent);
  }

  .strength-card:hover {
    border-color: rgba(0,170,255,0.35);
    box-shadow: 0 0 20px rgba(0,170,255,0.1);
    transform: translateY(-3px);
  }

  .strength-icon {
    font-size: 1.6rem;
    margin-bottom: 0.6rem;
    display: block;
  }

  .strength-name {
    font-family: 'Orbitron', monospace;
    font-size: 0.65rem;
    font-weight: 700;
    color: #00aaff;
    letter-spacing: 1px;
    text-transform: uppercase;
  }

  .strength-desc {
    font-size: 0.78rem;
    color: rgba(232,244,255,0.4);
    margin-top: 0.3rem;
    line-height: 1.4;
  }

  /* PROGRESS BARS */
  .about-skills-bar {
    margin-top: 1.5rem;
    opacity: 0;
    animation: aboutFadeUp 0.7s ease 0.7s both;
  }

  .skill-bar-item {
    margin-bottom: 1rem;
  }

  .skill-bar-header {
    display: flex;
    justify-content: space-between;
    margin-bottom: 0.4rem;
  }

  .skill-bar-name {
    font-size: 0.82rem;
    font-weight: 600;
    color: rgba(232,244,255,0.7);
    letter-spacing: 1px;
  }

  .skill-bar-pct {
    font-family: 'Orbitron', monospace;
    font-size: 0.7rem;
    color: #00aaff;
  }

  .skill-bar-track {
    height: 4px;
    background: rgba(0,170,255,0.1);
    border-radius: 2px;
    overflow: hidden;
  }

  .skill-bar-fill {
    height: 100%;
    background: linear-gradient(to right, #00aaff, #38c8ff);
    border-radius: 2px;
    box-shadow: 0 0 8px rgba(0,170,255,0.5);
    transform-origin: left;
    animation: barGrow 1.2s ease both;
  }

  @keyframes barGrow {
    from { transform: scaleX(0); }
    to { transform: scaleX(1); }
  }

  @keyframes aboutFadeUp {
    from { opacity: 0; transform: translateY(24px); }
    to { opacity: 1; transform: translateY(0); }
  }

  /* RESPONSIVE */
  @media (max-width: 900px) {
    .about-section { padding: 100px 2rem 60px; }
    .about-grid { grid-template-columns: 1fr; }
    .strengths-grid { grid-template-columns: repeat(2, 1fr); }
  }

  @media (max-width: 500px) {
    .strengths-grid { grid-template-columns: repeat(2, 1fr); }
  }
`;

const STRENGTHS = [
  { icon: "🧠", name: "Problem Solver", desc: "Breaks down complex challenges into solutions" },
  { icon: "⚡", name: "Fast Learner", desc: "Quickly adapts to new technologies" },
  { icon: "💡", name: "Tech-Driven", desc: "Passionate about emerging tech and innovation" },
  { icon: "🔄", name: "Adaptable", desc: "Thrives in dynamic and changing environments" },
];

const SKILL_BARS = [
  { name: "Artificial Intelligence & ML", pct: 75 },
  { name: "Internet of Things (IoT)", pct: 78 },
  { name: "Hardware & Embedded Systems", pct: 72 },
];

export default function About() {
  const sectionRef = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="about-section" id="about" ref={sectionRef}>
      <style>{styles}</style>
      <div className="about-container">

        {/* Header */}
        <div className="about-header">
          <p className="about-label">Get to know me</p>
          <h2 className="about-title">About Me</h2>
        </div>

        {/* Main Grid */}
        <div className="about-grid">

          {/* LEFT — Introduction */}
          <div className="about-intro-card">
            <p className="card-eyebrow">Self Introduction</p>
            <h3>Who I Am</h3>
            <p>
              I'm <span className="highlight-text">Vincent D. Manalo</span>, a 3rd year
              <span className="highlight-orange"> Computer Engineering student</span> with
              a deep passion for computers, artificial intelligence, and building technology
              that creates real-world impact.
            </p>
            <p>
              I chose Computer Engineering because of my strong interest in
              <span className="highlight-text"> computers and AI</span> — two fields that
              are shaping the future. My program has equipped me with both hardware and
              software knowledge, giving me a well-rounded technical foundation.
            </p>
            <p>
              Outside of academics, I enjoy exploring new technologies, working on
              hands-on projects, and constantly pushing the boundaries of what I can build —
              from <span className="highlight-orange">IoT systems</span> to
              <span className="highlight-text"> intelligent applications</span>.
            </p>

            {/* Skill Bars */}
            <div className="about-skills-bar">
              {SKILL_BARS.map((s, i) => (
                <div className="skill-bar-item" key={i}>
                  <div className="skill-bar-header">
                    <span className="skill-bar-name">{s.name}</span>
                    <span className="skill-bar-pct">{s.pct}%</span>
                  </div>
                  <div className="skill-bar-track">
                    <div
                      className="skill-bar-fill"
                      style={{
                        width: `${s.pct}%`,
                        animationDelay: `${0.8 + i * 0.15}s`,
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT */}
          <div className="about-right">

            {/* Fields of Interest */}
            <div className="about-interest-card">
              <p className="card-eyebrow">Focus Areas</p>
              <h3 style={{ fontFamily: "'Orbitron', monospace", fontSize: "1rem", fontWeight: 700, color: "#e8f4ff" }}>
                Fields of Interest
              </h3>
              <div className="interest-items">
                {[
                  { icon: "🧠", title: "Artificial Intelligence", desc: "Machine learning, neural networks, and intelligent systems" },
                  { icon: "📡", title: "Internet of Things", desc: "Connected devices, sensors, and smart systems" },                  
                  { icon: "🔧", title: "Hardware Engineering", desc: "Circuit design, microcontrollers, and prototyping" },
                ].map((item, i) => (
                  <div className="interest-item" key={i}>
                    <div className="interest-icon">{item.icon}</div>
                    <div className="interest-info">
                      <h4>{item.title}</h4>
                      <p>{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Career Goal */}
            <div className="about-goal-card">
              <p className="goal-label">// Career Aspiration</p>
              <p className="goal-text">
                My goal is to become a <span style={{ color: "#ffaa33", fontWeight: 600 }}>successful engineer and innovator</span> —
                someone who leverages AI and IoT to solve real-world problems and contribute
                to a smarter, more connected future.
              </p>
            </div>
          </div>
        </div>

        {/* Strengths */}
        <div className="about-strengths">
          <p className="strengths-title">// Personal Strengths</p>
          <div className="strengths-grid">
            {STRENGTHS.map((s, i) => (
              <div className="strength-card" key={i} style={{ animationDelay: `${0.6 + i * 0.1}s` }}>
                <span className="strength-icon">{s.icon}</span>
                <p className="strength-name">{s.name}</p>
                <p className="strength-desc">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>

      </div>
    </section>
  );
}